import { Check, X, Edit2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ADMIN_ADMISSION_FORM_OVERVIEW_ROUTE } from "../../../../../routes/RoutesPathes";
import { translateAgeString } from "../../../../../utils/exportUtils";
export const AdmissionTable = ({ data, onApprove, onReject, onEdit }) => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const isAr = i18n.language === "ar";

  const getStatusStyle = (status) => {
    const baseStyle = {
      padding: "4px 12px",
      borderRadius: "12px",
      fontSize: "11px",
      fontWeight: "600",
      display: "inline-block",
      textTransform: "capitalize",
    };

    switch (status) {
      case "approved":
        return { ...baseStyle, backgroundColor: "#e8f5e9", color: "#2e7d32" };
      case "rejected":
        return { ...baseStyle, backgroundColor: "#ffebee", color: "#c62828" };
      case "pending":
        return { ...baseStyle, backgroundColor: "#fff9e6", color: "#f57c00" };
      default:
        return { ...baseStyle, backgroundColor: "#f5f5f5", color: "#616161" };
    }
  };

  const getActionButtonStyle = (status, type) => {
    const isPending = status === "pending";
    let colors = { background: "#f5f5f5", hover: "#e0e0e0", icon: "#bbb" };

    if (isPending) {
      switch (type) {
        case "approve":
          colors = { background: "#e8f5e9", hover: "#c8e6c9", icon: "#2e7d32" };
          break;
        case "reject":
          colors = { background: "#ffebee", hover: "#ffcdd2", icon: "#c62828" };
          break;
        case "edit":
          colors = { background: "#e3f2fd", hover: "#bbdefb", icon: "#1565c0" };
          break;
        default:
          break;
      }
    }

    return {
      backgroundColor: colors.background,
      border: "none",
      borderRadius: "50%",
      width: "32px",
      height: "32px",
      cursor: isPending ? "pointer" : "not-allowed",
      transition: "all 0.15s ease",
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
      opacity: isPending ? 1 : 0.4,
      hoverBackground: colors.hover,
      iconColor: colors.icon,
    };
  };

  return (
    <div dir={isAr ? "rtl" : "ltr"}>
      <table className="w-100 text-color">
        <thead>
          <tr className="table-background">
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.child_name") || "Child Name"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.parents") || "Parents"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.parents_phone_numbers") || "Parents' Phone Numbers"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.parents_emails") || "Parents' Emails"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.room_names") || "Room Names"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.child_age") || "Child Age"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header">
              {t("table.status") || "Status"}
            </th>
            <th className="py-2 px-2 fw-bold fs-6 table-header text-center">
              {t("table.actions") || "Actions"}
            </th>
          </tr>
        </thead>

        <tbody>
          {data.map((row, index) => {
            const approveStyle = getActionButtonStyle(row.status, "approve");
            const rejectStyle = getActionButtonStyle(row.status, "reject");
            const editStyle = getActionButtonStyle(row.status, "edit");

            return (
              <tr
                key={row.id}
                className={index % 2 !== 0 ? "table-background" : ""}
              >
                
                <td 
                  className="py-2 px-2 text-12 fw-bold"
                  style={{ 
                    cursor: "pointer", 
                    transition: "color 0.2s ease",
                    color: "inherit" 
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.color = "#5e35b1"; 
                    e.target.style.textDecoration = "underline";
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.color = "inherit"; 
                    e.target.style.textDecoration = "none";
                  }}
                  onClick={() => navigate(`${ADMIN_ADMISSION_FORM_OVERVIEW_ROUTE}/${row.id}`)}
                >
                  {row.child_name}
                </td>

                <td className="py-2 px-2 text-12 fw-semibold">
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {/* Always show parent1 first */}
                    <div>{row.parent1_name || "-"}</div>
                    {/* Always show parent2 second */}
                    <div>{row.parent2_name || "-"}</div>
                  </div>
                </td>

                <td className="py-2 px-2 text-12 fw-semibold">
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {/* Always show parent1 phone first */}
                    <div>{row.parent1_phone || "-"}</div>
                    {/* Always show parent2 phone second */}
                    <div>{row.parent2_phone || "-"}</div>
                  </div>
                </td>

                {/* Made emails clickable to open mail dialog */}
                <td className="py-2 px-2 text-12 fw-semibold">
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {/* Always show parent1 email first - clickable */}
                    <div>
                      {row.parent1_email ? (
                        <a
                          href={`mailto:${row.parent1_email}`}
                          style={{
                            color: "#5e35b1",
                            textDecoration: "none",
                            cursor: "pointer",
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.textDecoration = "underline";
                            e.target.style.color = "#9b19d2ff";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.textDecoration = "none";
                            e.target.style.color = "#5e35b1";
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.parent1_email}
                        </a>
                      ) : (
                        "-"
                      )}
                    </div>
                    {/* Always show parent2 email second - clickable */}
                    <div>
                      {row.parent2_email ? (
                        <a
                          href={`mailto:${row.parent2_email}`}
                          style={{
                            color: "#5e35b1",
                            textDecoration: "none",
                            cursor: "pointer",
                            transition: "all 0.2s ease"
                          }}
                          onMouseEnter={(e) => {
                            e.target.style.textDecoration = "underline";
                            e.target.style.color = "#9b19d2ff";
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.textDecoration = "none";
                            e.target.style.color = "#5e35b1";
                          }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          {row.parent2_email}
                        </a>
                      ) : (
                        "-"
                      )}
                    </div>
                  </div>
                </td>

                {/*  Display room_names with line breaks (stacked vertically) */}
                <td className="py-2 px-2 text-12 fw-semibold">
                  <div style={{ display: "flex", flexDirection: "column", gap: "4px" }}>
                    {row.room_names ? 
                      row.room_names.split(',').map((room, idx) => (
                        <div key={idx}>{room.trim()}</div>
                      )) 
                      : "-"
                    }
                  </div>
                </td>

                <td className="py-2 px-2 text-12 fw-semibold">
                  {translateAgeString(row.child_age)}
                </td>
                <td className="py-2 px-2 text-12 fw-semibold">
                  <span style={getStatusStyle(row.status)}>
                    { t(`status.${row.status}`) }
                  </span>
                </td>
                <td className="py-2 px-2 text-12 fw-semibold">
                  <div className="d-flex gap-2 justify-content-center">
                    <button
                      title={isAr ? "قبول " : "Approve"}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.status === "pending") onApprove(row);
                      }}
                      disabled={row.status !== "pending"}
                      style={{ ...approveStyle }}
                      onMouseEnter={(e) => {
                        if (row.status === "pending") {
                          e.currentTarget.style.backgroundColor = approveStyle.hoverBackground;
                          e.currentTarget.style.transform = "scale(1.1)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = approveStyle.backgroundColor;
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <Check size={16} color={approveStyle.iconColor} strokeWidth={2.5} />
                    </button>

                    <button
                      title={isAr ? "رفض " : "Reject"}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.status === "pending") onReject(row);
                      }}
                      disabled={row.status !== "pending"}
                      style={{ ...rejectStyle }}
                      onMouseEnter={(e) => {
                        if (row.status === "pending") {
                          e.currentTarget.style.backgroundColor = rejectStyle.hoverBackground;
                          e.currentTarget.style.transform = "scale(1.1)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = rejectStyle.backgroundColor;
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <X size={16} color={rejectStyle.iconColor} strokeWidth={2.5} />
                    </button>

                    <button
                      title={isAr ? "تعديل " : "Edit"}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (row.status === "pending") {
                          navigate(`${ADMIN_ADMISSION_FORM_OVERVIEW_ROUTE}/${row.id}`, { state: { editMode: true } });
                        }
                      }}
                      disabled={row.status !== "pending"}
                      style={{ ...editStyle }}
                      onMouseEnter={(e) => {
                        if (row.status === "pending") {
                          e.currentTarget.style.backgroundColor = editStyle.hoverBackground;
                          e.currentTarget.style.transform = "scale(1.1)";
                        }
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = editStyle.backgroundColor;
                        e.currentTarget.style.transform = "scale(1)";
                      }}
                    >
                      <Edit2 size={14} color={editStyle.iconColor} strokeWidth={2.5} />
                    </button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};